import { api } from '../lib/api';

/** GET /v1/me */
export interface Me {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  /** Null until the welcome steps are done; the app sends the person there until then. */
  onboardedAt: string | null;
  createdAt: string;
  memberships: Array<{ orgId: string; role: string; org: { name: string; slug: string } }>;
  /** A paid seat on a live plan: uploads, editing and transcripts are open. */
  paidSeat: boolean;
  /**
   * Where that plan comes from: a seat on a team that pays for it, a plan an
   * admin gave, or the free plan.
   */
  planSource: 'seat' | 'comp' | 'none';
  /** When a comped plan lapses. Null for a seat, the free plan, or a grant with no expiry. */
  planUntil: string | null;
  /** Whether a sign-in has proven the address. Google accounts arrive proven. */
  emailVerified: boolean;
  /** When an unproven address stops being able to record. Null once verified. */
  verifyBy: string | null;
}

export function fetchMe(): Promise<Me> {
  return api<Me>('/v1/me');
}

/**
 * POST /v1/auth/password-reset: ask Slate to mail a branded reset link.
 *
 * Answers the same whether or not the address has an account, so the page
 * that calls it can only ever say "if there is an account, it is on its way".
 * Throws ApiError('not_configured') where the API has no service account and
 * cannot mint the code; AuthProvider falls back to Firebase's own email.
 */
export function requestPasswordReset(email: string): Promise<void> {
  return api<void>('/v1/auth/password-reset', { method: 'POST', body: { email }, auth: 'none' });
}

/**
 * POST /v1/me/verify-email: ask Slate to mail the confirmation link. Throws
 * ApiError('not_configured') where the API cannot mint the code; the caller
 * falls back to Firebase's own email.
 */
export function requestEmailVerification(): Promise<{ ok: boolean; alreadyVerified: boolean }> {
  return api('/v1/me/verify-email', { method: 'POST' });
}

export function updateMe(body: { name?: string | null }): Promise<Me> {
  return api<Me>('/v1/me', { method: 'PATCH', body });
}

/** The image goes up as base64 in JSON; the API caps it at 2 MB. */
export async function uploadAvatar(file: Blob): Promise<Me> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(file);
  });
  return api<Me>('/v1/me/avatar', { method: 'PUT', body: { contentType: file.type, data } });
}

export function removeAvatar(): Promise<Me> {
  return api<Me>('/v1/me/avatar', { method: 'DELETE' });
}

/** The welcome steps are done; the app stops sending the person back to them. */
export function completeOnboarding(): Promise<Me> {
  return api<Me>('/v1/me/onboarded', { method: 'POST' });
}

export function deleteAccount(): Promise<void> {
  return api<void>('/v1/me', { method: 'DELETE' });
}

/** Something that has to be dealt with before the account can go. */
export interface DeletionBlocker {
  kind: 'subscription' | 'sole_owner';
  id: string;
  name: string;
  slug: string;
}

/** What stands in the way of deleting the account, asked before offering to. */
export function fetchDeletionBlockers(): Promise<{ blockers: DeletionBlocker[] }> {
  return api<{ blockers: DeletionBlocker[] }>('/v1/me/deletion');
}
